import classNames from 'classnames';
import dayjs from 'dayjs';
import { Badge, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faTemperatureHigh,
  faPlugCircleBolt,
  faUserSlash,
  faPowerOff,
  faLayerGroup,
  faRulerCombined,
  faBolt,
  faFileCircleExclamation,
  faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import RevealDropdown from '../base/RevealDropdown';

const SEVERITY = {
  CRITICAL: { icon: faCircleExclamation, cls: 'danger', label: 'Critical' },
  WARNING: { icon: faTriangleExclamation, cls: 'warning', label: 'Warning' },
  INFO: { icon: faCircleInfo, cls: 'info', label: 'Info' }
};

// A machine has no avatar, so the slot the theme gives to a face is used for the
// thing that actually identifies an alert at a glance: what kind of fault it is.
const RULE_ICON = {
  TEMP_RUNAWAY: faPlugCircleBolt,
  TEMP_DEVIATION: faTemperatureHigh,
  TEMP_SENSOR_FAULT: faTemperatureHigh,
  UNATTRIBUTED_PRODUCTION: faUserSlash,
  MACHINE_OFFLINE: faPowerOff,
  QUEUE_EMPTY: faLayerGroup,
  DIAMETER_DEVIATION: faRulerCombined,
  GAUGE_FAULT: faRulerCombined,
  SPARK_FAULT_BURST: faBolt,
  SETPOINT_OFF_STANDARD: faFileCircleExclamation
};

const RULE_LABEL = {
  MACHINE_OFFLINE: 'not responding',
  BREAKDOWN: 'stopped on an alarm',
  MACHINE_STOPPED: 'stopped with work loaded',
  UNATTRIBUTED_PRODUCTION: 'producing with no operator',
  QUEUE_EMPTY: 'empty queue',
  TEMP_DEVIATION: 'temperature off setpoint',
  TEMP_RUNAWAY: 'suspected shorted relay',
  TEMP_SENSOR_FAULT: 'temperature sensor fault',
  DIAMETER_DEVIATION: 'cable off size',
  GAUGE_FAULT: 'laser gauge fault',
  SPARK_FAULT_BURST: 'spark tester firing repeatedly',
  SETPOINT_OFF_STANDARD: 'set off the standard'
};

const ago = ts => {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - Number(ts)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

/**
 * One alert in the theme's notification layout.
 *
 * The time shown is `firstSeenAt` — when the problem started, not when we last
 * looked at it. An alert open since 06:00 reads very differently from one that
 * appeared a minute ago, and both are equally "current".
 */
const AlertNotificationItem = ({ alert, className, type = 'pageItem', onAck }) => {
  const sev = SEVERITY[alert.severity] || SEVERITY.INFO;
  const acked = alert.status === 'ACKNOWLEDGED';
  const resolved = alert.status === 'RESOLVED';
  const page = type === 'pageItem';
  const started = alert.firstSeenAt ? dayjs(Number(alert.firstSeenAt)) : null;
  const recurrence = alert.data?.recurrence;

  return (
    <div
      className={classNames(className, 'py-3 notification-card position-relative', {
        // the theme's "unread" tint carries the meaning it already had: this one
        // has not been looked at yet
        unread: !acked && !resolved,
        'opacity-75': resolved,
        'px-4 px-lg-6': page,
        'px-2 px-sm-3': !page
      })}
    >
      <div className="d-flex align-items-center justify-content-between position-relative">
        <div className="d-flex">
          <div
            className={classNames(
              `d-flex align-items-center justify-content-center rounded-circle me-3 flex-shrink-0 bg-${sev.cls}-subtle text-${sev.cls}`
            )}
            style={page ? { width: 48, height: 48 } : { width: 32, height: 32 }}
            title={sev.label}
          >
            <FontAwesomeIcon
              icon={RULE_ICON[alert.rule] || sev.icon}
              className={page ? 'fs-6' : 'fs-9'}
            />
          </div>

          <div className={classNames('flex-1', { 'me-sm-3': !page, 'mt-1 me-2': page })}>
            <h4 className="fs-9 text-body-emphasis mb-1">
              {String(alert.machineId || '').toUpperCase()}
              <span className="ms-2 fw-normal text-body-quaternary fs-10">
                {RULE_LABEL[alert.rule] || alert.rule}
              </span>
              {resolved ? (
                <Badge bg="success" className="ms-2 fs-10">
                  resolved
                </Badge>
              ) : acked ? (
                <Badge
                  bg="secondary"
                  className="ms-2 fs-10"
                  title={`Acknowledged by ${alert.acknowledgedBy || 'someone'}`}
                >
                  acknowledged
                </Badge>
              ) : null}
              {recurrence >= 3 ? (
                <Badge bg="danger" className="ms-2 fs-10" title="This keeps coming back">
                  <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                  {recurrence}× in 24h
                </Badge>
              ) : null}
            </h4>

            <p className="fs-9 text-body-highlight mb-2 mb-sm-3 fw-normal">
              <span className="fw-bold">{alert.title}</span>
              {page && alert.detail ? (
                <span className="ms-1 text-body-secondary">{alert.detail}</span>
              ) : null}
              <span className="ms-2 text-body-quaternary text-opactity-75 fw-bold fs-10">
                {ago(alert.firstSeenAt)}
              </span>
            </p>

            <p className="text-body-secondary fs-9 mb-0">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              {started ? (
                <>
                  <span className="fw-bold">{started.format('HH:mm')} </span>
                  {started.format('DD MMM, YYYY')}
                </>
              ) : (
                <span className="fw-bold">unknown</span>
              )}
              {alert.occurrences > 1 ? (
                <span className="ms-2 text-body-quaternary">
                  still true after {alert.occurrences} checks
                </span>
              ) : null}
            </p>
          </div>
        </div>

        <RevealDropdown
          btnClassName="notification-dropdown-toggle"
          dropdownMenuClassName="mt-2"
        >
          <Dropdown.Item
            disabled={acked || resolved || !onAck}
            onClick={() => onAck?.(alert)}
          >
            Acknowledge
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item disabled className="fs-10 text-body-quaternary">
            {/* said plainly, because the difference is the whole point */}
            Alerts close themselves when the machine recovers
          </Dropdown.Item>
        </RevealDropdown>
      </div>
    </div>
  );
};

export default AlertNotificationItem;
